import {
    ApplicationConfig,
    provideBrowserGlobalErrorListeners,
    provideZoneChangeDetection,
    LOCALE_ID, provideAppInitializer, inject
} from '@angular/core';
import { provideRouter, withViewTransitions } from '@angular/router';
import { appRoutes } from './app.routes';
import { APP_CONFIG } from '../env/env.token';
import { env } from '../env/env';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { TRADGINE_DATA_API_BASE_URL } from '@tradgine/data-access/tradgine-data';
import { AppConfig } from '../env/app-config.model';
import { authInterceptor } from './auth/interceptors/auth.interceptor';
import { AUTO_BID_API_BASE_URL } from '@tradgine/data-access/auto-bid';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { AUTH_BASE_URL, AuthenticateApi } from '@tradgine/data-access/auth';
import { AUCTION_FLOW_API_BASE_URL } from '@tradgine/data-access/auction-flow';
import { AUCTION_SEALED_FLOW_API_BASE_URL } from '@tradgine/data-access/auction-sealed-flow';
import { HISTORY_API_BASE_URL } from '@tradgine/data-access/history';
import { provideAuctionTrade } from './infrastructure/auction-trade/providers';
import { provideSealedAuctionTrade } from './infrastructure/sealed-auction-trade/providers';
import { provideTradingProcedureListInfrastructure } from './infrastructure/auction/provider';
import { acceptLanguageInterceptor } from './infrastructure/common/interceptors/accept-language.interceptor';
import { ALERT_SERVICE } from '../shared/alert/alert-service.token';
import { AlertService } from '../shared/alert/alert.service';
import { httpErrorInterceptor } from '../core/interceptors/http-error.interceptor';
import { TimeService } from '@tradgine/time/core';
import { createHttpServerTime$ } from '@tradgine/time/adapters';
import { authErrorInterceptor } from './auth/interceptors/auth-error.interceptor';
import { filter, map } from 'rxjs/operators';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAppInitializer(() => {
            const time = inject(TimeService);
            const authApi = inject(AuthenticateApi);
            const getServerTime$ = () =>
                authApi.getServerTime().pipe(
                    filter((dto) => dto.isOk()),
                    map((result) => new Date(result.unwrap().utc))
                );
            time.connect(createHttpServerTime$(getServerTime$, 60_000));
        }),
        provideHttpClient(
            withInterceptors([
                authInterceptor,
                acceptLanguageInterceptor,
                httpErrorInterceptor,
                authErrorInterceptor,
            ])
        ),
        provideBrowserGlobalErrorListeners(),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(appRoutes, withViewTransitions()),
        {
            provide: LOCALE_ID,
            useFactory: () => {
                const lang = document?.documentElement?.lang || navigator.language || 'en';
                return lang.startsWith('es') ? 'es-ES' : 'en-GB';
            },
        },
        {
            provide: APP_CONFIG,
            useValue: env,
        },
        {
            provide: AUTH_BASE_URL,
            useFactory: (cfg: AppConfig) => cfg.authUrl,
            deps: [APP_CONFIG],
        },
        {
            provide: TRADGINE_DATA_API_BASE_URL,
            useFactory: (cfg: AppConfig) => cfg.tradgineDataUrl,
            deps: [APP_CONFIG],
        },
        {
            provide: AUCTION_FLOW_API_BASE_URL,
            useFactory: (cfg: AppConfig) => cfg.auctionFlowUrl,
            deps: [APP_CONFIG],
        },
        {
            provide: AUTO_BID_API_BASE_URL,
            useFactory: (cfg: AppConfig) => cfg.autoBidUrl,
            deps: [APP_CONFIG],
        },
        {
            provide: AUCTION_SEALED_FLOW_API_BASE_URL,
            useFactory: (cfg: AppConfig) => cfg.auctionSealedFlowUrl,
            deps: [APP_CONFIG],
        },
        {
            provide: HISTORY_API_BASE_URL,
            useFactory: (cfg: AppConfig) => cfg.historyUrl,
            deps: [APP_CONFIG],
        },
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: { appearance: 'outline' },
        },
        {
            provide: ALERT_SERVICE,
            useClass: AlertService
        },
        provideTradingProcedureListInfrastructure(),
        provideAuctionTrade(),
        provideSealedAuctionTrade(),
    ],
};
